import { useState, useEffect } from 'react';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * Returns all rooms the user has joined for the given contest,
 * each with its member IDs. Used by the per-room ranking switcher.
 * Non-realtime — refreshed when userId or contestId changes.
 */
export const useMyRooms = (userId, contestId) => {
  // myRooms: same-contest rooms (used by ranking switcher)
  // allMyRoomIds: every room the user is in regardless of contest (used by emoji fan-out)
  const [myRooms, setMyRooms] = useState([]);
  const [allMyRoomIds, setAllMyRoomIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setMyRooms([]); setAllMyRoomIds([]); setLoading(false); return; }
    let cancelled = false;

    const load = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        const roomIds = userDoc.data()?.roomIds || [];
        const sameContest = [];
        const allIds = [];
        for (const rid of roomIds) {
          const roomDoc = await getDoc(doc(db, 'rooms', rid));
          if (!roomDoc.exists()) continue;
          allIds.push(rid);
          const data = roomDoc.data();
          if (contestId && data.contestId !== contestId) continue;
          const membersSnap = await getDocs(collection(db, 'rooms', rid, 'members'));
          sameContest.push({
            id: rid,
            name: data.name,
            code: data.code,
            memberIds: membersSnap.docs.map(d => d.id),
          });
        }
        if (!cancelled) {
          setMyRooms(sameContest);
          setAllMyRoomIds(allIds);
        }
      } catch (e) {
        console.error('useMyRooms error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, contestId]);

  return { myRooms, allMyRoomIds, loading };
};
